import { action, computed, makeObservable, observable } from 'mobx'

import {
  IDefaultPermitType,
  IPermitType,
  IWorkflowStep,
  PermitActionType,
} from '~/client/graph/types.generated'
import OperationRule from '~/client/src/shared/models/OperationRule'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import { TagIconType } from '../../enums/TagIcon'
import { TagType } from '../../enums/TagType'
import Tag, { ITag } from '../../models/Tag'
import LiveListSyncGuard from '../../utils/LiveListSyncGuard'
import { reduceByCategories } from '../../utils/util'
import EventsStore from '../EventStore/Events.store'
import WorkflowCategoriesStore from './WorkflowCategories.store'

import Colors from '~/client/src/shared/theme.module.scss'

function compareByUpdatedAt(a: WorkflowType, b: WorkflowType): number {
  return b.updatedAt - a.updatedAt
}
function compareByOrderIndex(a: WorkflowType, b: WorkflowType): number {
  return a.orderIndex - b.orderIndex
}

export interface IWorkflowTypeRuleChanges {
  savedRules: OperationRule[]
  deletedRuleIds: string[]
}

export default class WorkflowTypesStore {
  private readonly projectWorkflowTypesMap = observable(
    new Map<string, WorkflowType>(),
  )
  private readonly defaultWorkflowTypesMap = observable(
    new Map<string, WorkflowType>(),
  )
  private readonly syncGuard = new LiveListSyncGuard()

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly workflowCategoriesStore: WorkflowCategoriesStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get allTypes(): WorkflowType[] {
    return [...this.defaultTypes, ...this.projectSpecificTypes]
  }

  @computed
  public get allTypesSortedByOrderIndex(): WorkflowType[] {
    return this.allTypes.sort(compareByOrderIndex)
  }

  @computed
  public get actualTypesAsTags(): ITag[] {
    return this.toTags(workflowType => workflowType.id)
  }

  /**
   * Tags for the notification / ball-in-court rules side, keyed by the stable
   * `type` rather than the version-specific id. Saving a workflow type mints a
   * new id, so a rule that stored an id stops matching every form created after
   * that save. `actualTypesAsTags` stays id-keyed on purpose: the workflow
   * filters and `tagListsByTagTypeMap` resolve those tag ids as type ids.
   */
  @computed
  public get actualTypesAsRuleTags(): ITag[] {
    return this.toTags(workflowType => workflowType.type)
  }

  /**
   * Resolves the permit-type token a rule stored: a `type`, or — for every rule
   * written before the rules side moved to `type` — the id of some version of
   * the lineage, current or superseded.
   */
  public getInstanceById = (instanceId: string): ITag => {
    const type =
      this.getActualTypeByAnyVersionId(instanceId)?.type ?? instanceId

    return this.actualTypesAsRuleTags.find(tag => tag.id === type)
  }

  @computed
  public get actualTypes(): WorkflowType[] {
    const basedOnFilteredTypes = this.allTypes.filter(
      ({ id, isDeleted, type }) =>
        !isDeleted && !!type && !this.allTypes.some(t => id === t.basedOn),
    )

    const types = reduceByCategories(basedOnFilteredTypes, t => [t.type, t])

    const latestActualTypes = Object.keys(types).reduce((list, typeKey) => {
      const filteredTypes = types[typeKey].sort(compareByUpdatedAt)

      list.push(filteredTypes[0])
      return list
    }, [] as WorkflowType[])

    const predefinedTypes = latestActualTypes
      .filter(t => !t.isCustom && !t.isSmartImport)
      .sort(compareByOrderIndex)
    const userCustomTypes = latestActualTypes
      .filter(t => t.isCustom || t.isSmartImport)
      .sort(compareByOrderIndex)

    return predefinedTypes
      .concat(userCustomTypes)
      .filter(type =>
        this.workflowCategoriesStore.isCategoryFlagEnabled(type.categoryId),
      )
  }

  // Ids of every current type — the universe the resource-control pickers seed
  // from and normalize "All" against. Deactivated types are listed AND
  // selectable, so narrowing this to the active ones would collapse "every
  // active type plus a deactivated one" to [] and silently drop the deactivated
  // pick. Shared as one computed so the seed and the save can't drift onto
  // different definitions of "everything".
  @computed
  public get actualTypeIds(): string[] {
    return this.actualTypes.map(workflowType => workflowType.id)
  }

  @computed
  public get enabledTypes(): WorkflowType[] {
    if (this.appState.isWorkflowsDisabled) {
      return []
    }

    const { isAdmin } = this.appState.userActiveProjectSettings

    // Creation-enabled, then narrowed to what this user may create. Not a
    // pickability rule — the selectors list `actualTypes` and every row is
    // selectable; `isEnabled` only drives the Active / Not Active banding.
    return this.actualTypes.filter(
      workflowType =>
        workflowType.isEnabled && (isAdmin || !workflowType.isAdminOnly),
    )
  }

  public get isDataReceived(): boolean {
    return !this.appState.loading.get(e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES)
  }

  @computed
  private get lastUpdatedTypeByTypeMap(): Map<string, WorkflowType> {
    const map = new Map<string, WorkflowType>()
    const sorted = [...this.allTypes].sort(compareByUpdatedAt)
    for (const pt of sorted) {
      if (!map.has(pt.type)) {
        map.set(pt.type, pt)
      }
    }
    return map
  }

  public getLastUpdatedTypeByType = (type: string): WorkflowType => {
    return this.lastUpdatedTypeByTypeMap.get(type)
  }

  public getActualTypeByType = (type: string): WorkflowType => {
    return this.actualTypes.find(pt => pt.type === type)
  }

  public getWorkflowTypeById = (workflowTypeId: string): WorkflowType => {
    return (
      this.defaultWorkflowTypesMap.get(workflowTypeId) ||
      this.projectWorkflowTypesMap.get(workflowTypeId)
    )
  }

  public getPermitTypeById = this.getWorkflowTypeById

  /**
   * Resolve any permit-type id — current or a superseded version — to its
   * current actual-type version via the stable `type`. Workflow types are
   * versioned: editing one mints a new id while `type` is preserved.
   * Returns undefined when the id resolves to no current type (deleted).
   */
  public getActualTypeByAnyVersionId = (id: string): WorkflowType => {
    const directMatch = this.actualTypes.find(t => t.id === id)
    if (directMatch) return directMatch

    const anyVersion = this.getWorkflowTypeById(id)
    if (!anyVersion?.type) return undefined

    return this.getActualTypeByType(anyVersion.type)
  }

  /**
   * Resolve any workflow-type id for DISPLAY: its current actual-type version, or — when
   * that no longer exists (a deleted type) — the stored version itself. Falling back to
   * getWorkflowTypeById keeps a stored id from silently vanishing from a chip / control.
   */
  public getDisplayTypeByAnyVersionId = (id: string): WorkflowType => {
    return this.getActualTypeByAnyVersionId(id) ?? this.getWorkflowTypeById(id)
  }

  /**
   * Check if a workflowTypeId matches a restriction's workflowIds,
   * accounting for workflow type versioning via the stable `type` string.
   */
  public isWorkflowTypeMatchingWorkflowIds(
    workflowTypeId: string,
    workflowIds: string[],
  ): boolean {
    if (!workflowIds?.length) return true // empty = applies to all
    if (workflowIds.includes(workflowTypeId)) return true // exact match

    const workflowType = this.getWorkflowTypeById(workflowTypeId)
    if (!workflowType?.type) return false

    return workflowIds.some(wfId => {
      const restrictionType = this.getWorkflowTypeById(wfId)
      return restrictionType?.type === workflowType.type
    })
  }

  public isPermitTypeMatchingWorkflowIds =
    this.isWorkflowTypeMatchingWorkflowIds

  @action
  public receiveDefaultTypes(dtos: IDefaultPermitType[]) {
    dtos.forEach(dto => {
      const workflowType = WorkflowType.fromDefault(dto)
      this.defaultWorkflowTypesMap.set(dto.id, workflowType)
    })
  }

  @action
  public clearProjectSpecificTypes() {
    this.projectWorkflowTypesMap.clear()
    this.syncGuard.reset()
  }

  @action
  public receiveProjectSpecificTypes(dtos: IPermitType[]) {
    this.clearProjectSpecificTypes()

    dtos.forEach(dto => {
      const workflowType = WorkflowType.fromDto(dto)
      this.projectWorkflowTypesMap.set(dto.id, workflowType)
    })
  }

  /** Call before re-reading the list; pass the token to syncProjectSpecificTypes. */
  public beginSync = (): number => {
    return this.syncGuard.beginSync()
  }

  /**
   * Same result as receiveProjectSpecificTypes, but without emptying the list
   * first. Versions changed since the re-read began keep their newer state,
   * and unchanged versions keep their instance.
   */
  @action
  public syncProjectSpecificTypes(dtos: IPermitType[], syncToken: number) {
    const { syncGuard } = this
    if (!syncGuard.tryApply(syncToken)) {
      return
    }

    const freshIds = new Set(dtos.map(dto => dto.id))

    Array.from(this.projectWorkflowTypesMap.keys()).forEach(id => {
      if (!freshIds.has(id) && !syncGuard.isChangedSince(id, syncToken)) {
        this.projectWorkflowTypesMap.delete(id)
      }
    })

    dtos.forEach(dto => {
      if (syncGuard.isChangedSince(dto.id, syncToken)) {
        return
      }

      const current = this.projectWorkflowTypesMap.get(dto.id)
      if (!current || current.updatedAt !== dto.updatedAt) {
        this.projectWorkflowTypesMap.set(dto.id, WorkflowType.fromDto(dto))
      }
    })
  }

  @action
  public receiveProjectSpecificType(id: string, dto: IPermitType) {
    if (dto) {
      this.syncGuard.markChanged(id)
      this.projectWorkflowTypesMap.set(id, WorkflowType.fromDto(dto))
    }
  }

  @action.bound
  public createCustomType(
    name: string,
    categoryId: string,
    basedOnType: WorkflowType,
    callbackFn?: (workflowTypes: WorkflowType[]) => void,
  ) {
    const newType = basedOnType.getCopy() as WorkflowType

    newType.id = null
    newType.basedOn = null
    newType.name = name
    newType.categoryId = categoryId
    newType.orderIndex = this.actualTypes.length
    newType.workflowSteps.forEach(s => (s.workflowRuleIds = []))

    this.saveMany([newType], callbackFn)
  }

  public buildDraftTypeFromImport(
    name: string,
    categoryId: string,
    importedSteps: IWorkflowStep[],
  ): WorkflowType {
    //TEMP: make keys lowercase, should probably be handled by the prompt
    //TEMP: rename _id to id, should probably be handled by the prompt
    const importedStepsString = JSON.stringify(importedSteps)
      .replace(/"([^"]+)":/g, function ($0, $1) {
        return '"' + $1.charAt(0).toLowerCase() + $1.slice(1) + '":'
      })
      .replace(/"_id":/g, '"id":')

    const steps = JSON.parse(importedStepsString)

    const projectId = this.appState.userActiveProjectSettings.projectId
    const uniqueType = `smart-import-${Date.now()}`
    const newType = new WorkflowType(
      null,
      uniqueType,
      name,
      projectId,
      true,
      null,
      0,
      steps,
      false,
      false,
      null,
      null,
      null,
      null,
      false,
      categoryId,
    )

    newType.orderIndex = this.actualTypes.length

    return newType
  }

  @action.bound
  public createCustomTypeFromImport(
    name: string,
    categoryId: string,
    importedSteps: IWorkflowStep[],
    callbackFn?: (workflowTypes: WorkflowType[]) => void,
  ) {
    const newType = this.buildDraftTypeFromImport(
      name,
      categoryId,
      importedSteps,
    )
    this.saveMany([newType], callbackFn)
  }

  /**
   * Saves the types. Ball-in-court rule changes made with them are written in the same
   * transaction: the rules their steps point at and the rules they stopped using.
   */
  @action.bound
  public saveMany(
    workflowTypes: WorkflowType[],
    callbackFn?: (workflowTypes: WorkflowType[]) => void,
    errorCallbackFn?: (error: unknown) => void,
    ruleChanges?: IWorkflowTypeRuleChanges,
  ) {
    if (!workflowTypes?.length) {
      return
    }

    const workflowTypesDtos = workflowTypes.map(type => type.getDto())

    const actions = workflowTypes.reduce((acc, type) => {
      type.workflowSteps.forEach(step => {
        if (!step.actions?.length) {
          return
        }

        const actionsForStep = {
          startClosureAction: undefined,
          endClosureAction: undefined,
        }

        step.actions.forEach(action => {
          const actionType = action.type

          delete action.type

          if (actionType === PermitActionType.StartClosureAction) {
            actionsForStep.startClosureAction = action
          } else if (actionType === PermitActionType.EndClosureAction) {
            actionsForStep.endClosureAction = action
          }
        })

        acc.push({
          permitTypeId: type.id, // Wire name pinned by PermitTypeActionsDTOInput until the GraphQL schema renames it.
          workflowStepId: step.id,
          actions: [actionsForStep],
        })
      })

      return acc
    }, [])

    this.eventsStore.dispatch(
      e.SAVE_WORKFLOW_TYPES,
      workflowTypesDtos,
      actions,
      callbackFn,
      errorCallbackFn,
      ruleChanges && {
        operationRules: ruleChanges.savedRules.map(OperationRule.toDto),
        deletedOperationRuleIds: ruleChanges.deletedRuleIds,
      },
    )
  }

  @action.bound
  public deleteTypes(typeIds: string[]) {
    if (!typeIds?.length) {
      return
    }

    // TODO: rework and move this logic to the back-end
    const idsToDelete = this.allTypes
      .filter(t => typeIds.includes(t.type))
      .map(t => t.id)

    this.eventsStore.dispatch(e.REMOVE_WORKFLOW_TYPES, idsToDelete)
  }

  @action.bound
  public markTypesAsDeleted(ids: string[]) {
    if (!ids?.length) {
      return
    }

    ids.forEach(id => {
      if (this.projectWorkflowTypesMap.has(id)) {
        this.syncGuard.markChanged(id)
        this.projectWorkflowTypesMap.get(id).isDeleted = true
      }
    })
  }

  @computed
  public get defaultTypes(): WorkflowType[] {
    return [...this.defaultWorkflowTypesMap.values()]
  }

  @computed
  private get projectSpecificTypes(): WorkflowType[] {
    return [...this.projectWorkflowTypesMap.values()]
  }

  @computed
  public get materialTransferTypes(): WorkflowType[] {
    return this.allTypes.filter(type => type.isMaterialTransfer)
  }

  private get appState() {
    return this.eventsStore.appState
  }

  private toTags(getTagId: (workflowType: WorkflowType) => string): ITag[] {
    return this.actualTypes.map(
      workflowType =>
        new Tag(
          getTagId(workflowType),
          workflowType.name,
          Colors.grey88,
          TagIconType.FormType,
          TagType.PermitType,
        ),
    )
  }
}
